(() => {
  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  if (!window.customElements.get('cart-threshold-bar')) {
    class CartThresholdBar extends HTMLElement {
      constructor() {
        super();
        this.onCartChanged = this.onCartChanged.bind(this);
      }

      static get observedAttributes() {
        return ['threshold-count', 'current-count'];
      }

      connectedCallback() {
        this.update();
        document.addEventListener('cart:change', this.onCartChanged);
      }

      disconnectedCallback() {
        document.removeEventListener('cart:change', this.onCartChanged);
      }

      attributeChangedCallback() {
        this.update();
      }

      get thresholdCount() {
        return parseInt(this.getAttribute('threshold-count') || '0', 10);
      }

      get currentCount() {
        return parseInt(this.getAttribute('current-count') || '0', 10);
      }

      onCartChanged(event) {
        this.setAttribute('current-count', String(event.detail.cart?.item_count || 0));
      }

      update() {
        const text = this.querySelector('[data-threshold-text]');
        const progress = this.querySelector('.cart-threshold-bar__progress');
        const threshold = this.thresholdCount;
        const total = this.currentCount;

        if (!text || !progress || !threshold) {
          return;
        }

        if (total >= threshold) {
          text.innerHTML = this.getAttribute('reached-message') || '';
          progress.style.setProperty('--progress', '1');
          return;
        }

        const remainingCount = threshold - total;
        const template = remainingCount === 1 ? this.getAttribute('singular-message') : this.getAttribute('plural-message');
        text.innerHTML = (template || '').replace(/\{\{\s*remaining_count\s*\}\}/g, String(remainingCount));
        progress.style.setProperty('--progress', String(total / threshold));
      }
    }

    window.customElements.define('cart-threshold-bar', CartThresholdBar);
  }

  const discountApplied = (cart, code) => {
    const normalized = String(code || '').trim().toUpperCase();
    if (!normalized || !cart) return false;

    const apps = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.discount_applications || [])
    ];

    if (apps.some((app) => String(app.title || app.code || '').toUpperCase() === normalized)) {
      return true;
    }

    if ((cart.discount_codes || []).some((entry) => String(entry.code || '').toUpperCase() === normalized && entry.applicable !== false)) {
      return true;
    }

    return (cart.items || []).some((item) =>
      (item.discounts || item.line_level_discount_allocations || []).some((discount) => {
        const title = discount.title || discount.discount_application?.title || discount.code;
        return String(title || '').toUpperCase() === normalized;
      })
    );
  };

  if (!window.customElements.get('cart-discount')) {
    class CartDiscount extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.form = this.querySelector('[data-discount-form]');
        this.input = this.querySelector('[data-discount-input]');
        this.applyButton = this.querySelector('[data-discount-apply]');
        this.message = this.querySelector('[data-discount-message]');
        this.list = this.querySelector('[data-discount-list]');

        this.applyButton?.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            this.applyCode();
          },
          { signal }
        );

        this.input?.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.applyCode();
            }
          },
          { signal }
        );

        this.list?.addEventListener(
          'click',
          (event) => {
            const button = event.target.closest('[data-discount-remove]');
            if (!button) return;
            event.preventDefault();
            this.removeCode(button.getAttribute('data-discount-remove') || '');
          },
          { signal }
        );

        this.refreshFromCart();
        document.addEventListener('cart:change', () => this.refreshFromCart(), { signal });
        document.addEventListener('cart:refresh', () => this.refreshFromCart(), { signal });
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      setBusy(isBusy) {
        if (!this.applyButton) return;
        this.applyButton.toggleAttribute('aria-busy', isBusy);
        this.applyButton.disabled = isBusy;
        if (this.input) this.input.disabled = isBusy;
      }

      showMessage(text, type = 'error') {
        if (!this.message) return;
        this.message.hidden = !text;
        this.message.textContent = text || '';
        this.message.dataset.status = type;
      }

      async fetchCart() {
        return (await fetch(`${root()}cart.js`, { credentials: 'same-origin' })).json();
      }

      async ensureSectionHtml(cart, sectionIds) {
        const sections = { ...(cart?.sections || {}) };

        await Promise.all(
          sectionIds
            .filter((sectionId) => sectionId && !sections[sectionId])
            .map(async (sectionId) => {
              const response = await fetch(`${root()}?section_id=${encodeURIComponent(sectionId)}`, {
                credentials: 'same-origin'
              });
              if (response.ok) {
                sections[sectionId] = await response.text();
              }
            })
        );

        cart.sections = sections;
        return cart;
      }

      async updateDiscount(discount) {
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          })
        );

        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

        try {
          const response = await fetch(`${root()}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              discount,
              sections: sectionsToBundle.join(','),
              sections_url: window.location.pathname || `${root()}cart`
            })
          });

          const responseJson = await response.json();

          if (!response.ok) {
            throw new Error(responseJson?.description || responseJson?.message || 'Discount could not be applied');
          }

          // Match theme ProductForm pattern: fresh cart.js + section HTML from the mutation.
          const cart = await this.fetchCart();
          cart.sections = responseJson.sections || {};
          await this.ensureSectionHtml(cart, sectionsToBundle);

          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', {
              bubbles: true,
              detail: {
                baseEvent: 'discount:update',
                cart
              }
            })
          );

          // Fallback if section HTML still couldn't drive a drawer replace.
          if (!sectionsToBundle.some((sectionId) => cart.sections?.[sectionId])) {
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }

          return cart;
        } finally {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        }
      }

      renderCodes(cart) {
        if (!this.list) return;

        const codes = (cart?.discount_codes || [])
          .filter((entry) => entry && entry.code)
          .map((entry) => ({
            code: entry.code,
            applicable: entry.applicable !== false
          }));

        if (!codes.length) {
          this.list.hidden = true;
          this.list.innerHTML = '';
          return;
        }

        this.list.hidden = false;
        this.list.innerHTML = codes
          .map(
            (entry) => `
            <li class="cart-drawer__discount-code${entry.applicable ? '' : ' is-invalid'}">
              <span>${entry.code}</span>
              <button type="button" class="link" data-discount-remove="${entry.code}">
                ${this.getAttribute('data-remove-label') || 'Remove'}
              </button>
            </li>
          `
          )
          .join('');
      }

      async refreshFromCart() {
        try {
          const cart = await this.fetchCart();
          this.renderCodes(cart);
        } catch (error) {
          // Keep UI quiet on background refresh failures.
        }
      }

      async applyCode() {
        const code = (this.input?.value || '').trim();
        const emptyLabel = this.getAttribute('data-empty-message') || 'Enter a discount code';
        const successLabel = this.getAttribute('data-success-message') || 'Discount applied';
        const errorLabel = this.getAttribute('data-error-message') || 'Enter a valid discount code';

        this.showMessage('');

        if (!code) {
          this.showMessage(emptyLabel, 'error');
          this.input?.focus();
          return;
        }

        this.setBusy(true);

        try {
          const existing = await this.fetchCart();
          const activeCodes = (existing.discount_codes || [])
            .filter((entry) => entry?.code && entry.applicable !== false)
            .map((entry) => entry.code);

          const nextCodes = Array.from(new Set([...activeCodes, code]));
          const cart = await this.updateDiscount(nextCodes.join(','));

          // Drawer content may have been replaced; prefer the live element for UI feedback.
          const live = document.querySelector('cart-discount') || this;

          if (!discountApplied(cart, code)) {
            live.showMessage?.(errorLabel, 'error');
            live.renderCodes?.(cart);
            return;
          }

          if (live.input) live.input.value = '';
          live.showMessage?.(successLabel, 'success');
          live.renderCodes?.(cart);
        } catch (error) {
          const live = document.querySelector('cart-discount') || this;
          live.showMessage?.(error?.message || errorLabel, 'error');
        } finally {
          const live = document.querySelector('cart-discount') || this;
          live.setBusy?.(false);
          this.setBusy(false);
        }
      }

      async removeCode(codeToRemove) {
        this.setBusy(true);
        this.showMessage('');

        try {
          const existing = await this.fetchCart();
          const nextCodes = (existing.discount_codes || [])
            .map((entry) => entry.code)
            .filter((code) => code && code.toUpperCase() !== String(codeToRemove).toUpperCase());

          const cart = await this.updateDiscount(nextCodes.join(','));
          const live = document.querySelector('cart-discount') || this;
          live.renderCodes?.(cart);
        } catch (error) {
          const live = document.querySelector('cart-discount') || this;
          live.showMessage?.(error?.message || 'Unable to remove discount', 'error');
        } finally {
          const live = document.querySelector('cart-discount') || this;
          live.setBusy?.(false);
          this.setBusy(false);
        }
      }
    }

    window.customElements.define('cart-discount', CartDiscount);
  }

  if (!window.customElements.get('cart-recently-viewed')) {
    class CartRecentlyViewed extends HTMLElement {
      connectedCallback() {
        this.loadProducts();
      }

      getSectionId() {
        return (
          this.getAttribute('section-id') ||
          this.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '') ||
          ''
        );
      }

      buildQuery() {
        const stored = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]');
        const exclude = new Set(
          String(this.getAttribute('exclude-ids') || '')
            .split(',')
            .map((value) => parseInt(value, 10))
            .filter(Boolean)
        );
        const limit = parseInt(this.getAttribute('products-count') || '4', 10) || 4;

        return stored
          .map((id) => parseInt(id, 10))
          .filter((id) => id && !exclude.has(id))
          .slice(0, limit)
          .map((id) => `id:${id}`)
          .join(' OR ');
      }

      async loadProducts() {
        if (this.dataset.loaded === 'true') return;
        this.dataset.loaded = 'true';

        const query = this.buildQuery();
        const sectionId = this.getSectionId();

        if (!query || !sectionId) {
          this.remove();
          return;
        }

        try {
          const url = `${root()}search?type=product&q=${encodeURIComponent(query)}&section_id=${encodeURIComponent(sectionId)}`;
          const response = await fetch(url, { priority: 'low', credentials: 'same-origin' });
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const next = doc.querySelector('cart-recently-viewed');

          if (next && next.childElementCount > 0) {
            this.replaceChildren(...document.importNode(next, true).childNodes);
            this.hidden = false;
          } else {
            this.remove();
          }
        } catch (error) {
          this.remove();
        }
      }
    }

    window.customElements.define('cart-recently-viewed', CartRecentlyViewed);
  }

  const syncWhatsAppWithCartDrawer = () => {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    const update = () => {
      const isOpen =
        drawer.hasAttribute('open') ||
        drawer.getAttribute('aria-hidden') === 'false' ||
        drawer.classList.contains('is-open');
      document.documentElement.classList.toggle('cart-drawer-open', Boolean(isOpen));
    };

    update();
    drawer.addEventListener('dialog:after-show', update);
    drawer.addEventListener('dialog:after-hide', update);
    new MutationObserver(update).observe(drawer, {
      attributes: true,
      attributeFilter: ['open', 'aria-hidden', 'class']
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncWhatsAppWithCartDrawer, { once: true });
  } else {
    syncWhatsAppWithCartDrawer();
  }
})();
